import { useMemo } from 'react';

import { AspectRatio, Box, Title } from '@mantine/core';
import { IconCircleCheck, IconLock } from 'ui-icons';

import { ButtonMantine, ButtonProps } from '../../../../Buttons';
import { Card, ImageMantine, ThemeIcon } from '../../../../DataDisplay';
import { Group, Stack } from '../../../../Layout';
import { Popover } from '../../../../Overlays/Popover/Popover';
import { Text } from '../../../../Typography';

interface MerchantCardButtonProps extends ButtonProps {
  onClick?: () => void;
  ctaText?: string;
  disabled?: boolean;
}

interface MerchantCardBaseProps {
  logoSrc?: string;
  name: string;
  tagline?: string;
  domain?: string;
  buttonProps?: MerchantCardButtonProps;
  onTitleClick?: () => void;
  isClaimed?: boolean;
  isLocked?: boolean;
}

type LocalMerchantCardProps = Omit<MerchantCardBaseProps, 'domain'>;

const MerchantCardBase = ({
  logoSrc,
  name,
  tagline,
  domain,
  buttonProps: { ctaText, onClick, disabled, ...buttonProps } = {},
  onTitleClick,
  isClaimed = false,
  isLocked = false,
}: MerchantCardBaseProps) => {
  const rightSide = useMemo(() => {
    if (isLocked) {
      return (
        <ThemeIcon size={'xl'} c="gray.4" bg="transparent">
          <IconLock />
        </ThemeIcon>
      );
    }

    if (ctaText && !isClaimed) {
      return (
        <Group miw={96}>
          <ButtonMantine
            size="md"
            variant="outline"
            onClick={onClick}
            disabled={disabled}
            {...buttonProps}
          >
            {ctaText}
          </ButtonMantine>
        </Group>
      );
    }

    if (isClaimed) {
      return (
        <ThemeIcon size={'xl'} c="mint" p={0} bg="transparent">
          <IconCircleCheck />
        </ThemeIcon>
      );
    }

    return null;
  }, [buttonProps, ctaText, disabled, isClaimed, isLocked, onClick]);

  return (
    <Group gap="sm" justify="space-between" wrap="nowrap">
      <Group gap="sm" wrap="nowrap">
        <AspectRatio ratio={1} style={{ flexGrow: 0 }} miw={48}>
          <ImageMantine w={48} radius={12} src={logoSrc} alt={name} />
        </AspectRatio>
        <Stack gap={4} style={{ flexGrow: 1 }}>
          <Text
            fz="md"
            fw={600}
            ta="left"
            onClick={onTitleClick}
            component="a"
            style={{
              cursor: 'pointer',
              flex: 1,
            }}
            lineClamp={1}
          >
            {name}
          </Text>
          <Group>
            <Text type="label" c="cobalt" truncate="end">{`Earn ${tagline}`}</Text>
            {domain && (
              <Text truncate type="label" c="gray" visibleFrom="sm" style={{ flex: 1 }}>
                {domain}
              </Text>
            )}
          </Group>
        </Stack>
      </Group>
      {rightSide}
    </Group>
  );
};

const OnlineMerchantCard = ({
  logoSrc,
  name,
  tagline,
  domain,
  buttonProps: { onClick, disabled } = {},
  onTitleClick,
}: MerchantCardBaseProps) => {
  return (
    <Card w="100%" px={0} py={'md'}>
      <MerchantCardBase
        logoSrc={logoSrc}
        name={name}
        tagline={tagline}
        domain={domain}
        onTitleClick={onTitleClick}
        buttonProps={{
          // TODO-NOW: (Earn) Get Product requirements for Visit Site Button
          // Considering that the merchant modals are a thing, the Visit Site button skips the modals and opens sites directly. Should we have similar functionality?
          // assignees: GwaanByte, Dylan-Brandenburg
          ctaText: 'Shop',
          onClick: onClick,
          disabled: disabled,
        }}
      />
    </Card>
  );
};

const LocalMerchantCard = ({
  logoSrc,
  name,
  tagline,
  buttonProps: { onClick, disabled } = {},
  isClaimed,
  onTitleClick,
  isLocked,
}: LocalMerchantCardProps) => {
  return (
    <Card w="100%" px={0} py={'md'}>
      <MerchantCardBase
        logoSrc={logoSrc}
        name={name}
        tagline={tagline}
        onTitleClick={onTitleClick}
        buttonProps={{
          ctaText: 'Activate',
          onClick: onClick,
          disabled: disabled,
        }}
        isClaimed={isClaimed}
        isLocked={isLocked}
      />
    </Card>
  );
};
interface InStoreMerchantMarkerCardProps {
  logoSrc?: string;
  name: string;
  tagline?: string;
  onClick?: () => void;
}

/**
 * This is the card used on the map, which has inherently different styles than the other merchant cards.
 */
const MerchantHoverCard = ({ logoSrc, name, tagline, onClick }: InStoreMerchantMarkerCardProps) => {
  return (
    <Popover.Dropdown onClick={onClick} style={{ cursor: 'pointer' }}>
      <Box p="xs">
        <Group wrap="nowrap" gap="xs">
          <ImageMantine w={48} h={48} radius={12} fit="contain" src={logoSrc} alt={name} />
          <Stack gap={0}>
            <Title order={4} fz={12}>
              {name}
            </Title>
            <Group wrap="nowrap">
              <Text c="cobalt" fz={12} fw={600}>
                {tagline}
              </Text>
            </Group>
          </Stack>
        </Group>
      </Box>
    </Popover.Dropdown>
  );
};

export { LocalMerchantCard, MerchantCardBase, MerchantHoverCard, OnlineMerchantCard };
